import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { focusKeys, focusMeQueryOptions } from '@/entities/focus-session'
import { pomodoroQueryOptions } from '@/entities/pomodoro'
import { soundscapeKeys } from '@/entities/soundscape'
import { useEnterFocus } from '@/features/enter-focus'
import { useSoundscapePlayer } from '@/features/play-soundscape'
import { apiPost } from '@/shared/api'

// Раз в минуту: сервер закрывает сессию после пяти минут молчания, так что
// частить незачем, а пережить короткий обрыв связи запас позволяет.
const HEARTBEAT_MS = 60_000

// «В фокусе» = играет Endel или идёт помодоро. Сессия открывается по первому из
// событий и закрывается, когда прекратились оба, — сотруднику не нужно помнить
// про отдельную кнопку «войти в фокус».
export function useFocusAutotrack() {
  const qc = useQueryClient()
  const enter = useEnterFocus()

  const { data: me } = useQuery(focusMeQueryOptions)
  const { data: pomodoro } = useQuery(pomodoroQueryOptions)
  const { slug, playing } = useSoundscapePlayer()

  const pomodoroActive = pomodoro?.session != null && pomodoro.session.state !== 'stopped'
  const active = playing || pomodoroActive

  const heartbeat = useMutation({
    mutationFn: (soundscapeSlug: string) =>
      apiPost('/focus/heartbeat', { soundscape_slug: soundscapeSlug }),
  })

  // Открытие сессии. Гонку двойного старта закрывает сервер (одна running на
  // сотрудника), поэтому достаточно не слать второй запрос, пока идёт первый.
  const startingRef = useRef(false)
  useEffect(() => {
    if (!active || me || startingRef.current || enter.isPending) return
    startingRef.current = true
    enter.mutate(
      { withPomodoro: false },
      {
        onSettled: () => {
          startingRef.current = false
        },
      },
    )
  }, [active, me, enter])

  // Heartbeat, пока что-то идёт: он же сообщает серверу играющий режим.
  // Слаг и мутация живут в ref'ах: heartbeat.mutate меняется на каждом рендере
  // и, попав в зависимости эффекта, рестартовал бы интервал — период никогда не
  // истекал бы. Запись только в эффекте: в рендере трогать ref нельзя.
  const slugRef = useRef<string | null>(null)
  const beatRef = useRef(heartbeat.mutate)
  useEffect(() => {
    slugRef.current = playing ? slug : null
    beatRef.current = heartbeat.mutate
  })

  useEffect(() => {
    if (!active || !me) return

    const tick = () => beatRef.current(slugRef.current ?? '')
    tick()
    const id = window.setInterval(tick, HEARTBEAT_MS)
    return () => window.clearInterval(id)
  }, [active, me])

  // Оба источника смолкли — закрываем сессию и обновляем сводку времени.
  const stop = useMutation({
    mutationFn: (sessionId: string) => apiPost('/focus/stop', { session_id: sessionId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: focusKeys.all })
      void qc.invalidateQueries({ queryKey: soundscapeKeys.stats() })
    },
  })

  const stoppingRef = useRef(false)
  useEffect(() => {
    if (active || !me || stoppingRef.current || stop.isPending) return
    stoppingRef.current = true
    stop.mutate(me.id, {
      onSettled: () => {
        stoppingRef.current = false
      },
    })
  }, [active, me, stop])
}
